import { motion } from "framer-motion"
import { Phone, Mail, MapPin, Clock, Send, MessageCircle } from "lucide-react"
import { SiWhatsapp, SiFacebook, SiInstagram, SiX, SiSnapchat } from "react-icons/si"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Card, CardContent } from "@/components/ui/card"
import { fadeInUp, staggerContainer, staggerItem } from "@/lib/motion"
import { useState } from "react"

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    company: "",
    serviceType: "none",
    message: "",
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const whatsappMessage = `مرحباً، أنا ${formData.name}%0aالشركة: ${formData.company}%0aالهاتف: ${formData.phone}%0aالخدمة المطلوبة: ${formData.serviceType}%0aالرسالة: ${formData.message}`
    window.open(`https://wa.me/966550096297?text=${whatsappMessage}`, "_blank")
  }

  const contactInfo = [
    {
      icon: Phone,
      titleAr: "الهاتف",
      valueAr: "+966 55 009 6297",
      link: "tel:+966550096297",
    },
    {
      icon: Mail,
      titleAr: "البريد الإلكتروني",
      valueAr: "info@atyafalkhabbazin.sa",
      link: "mailto:info@atyafalkhabbazin.sa",
    },
    {
      icon: MapPin,
      titleAr: "العنوان",
      valueAr: "الرياض، المملكة العربية السعودية",
      link: "#",
    },
    {
      icon: Clock,
      titleAr: "ساعات العمل",
      valueAr: "السبت - الخميس: 7 صباحاً - 10 مساءً",
      link: "#",
    },
  ]

  const serviceOptions = [
    { value: "corporate", label: "كيترنج الشركات" },
    { value: "coffee-break", label: "كوفي بريك الاجتماعات" },
    { value: "weddings", label: "أعراس ومناسبات" },
    { value: "cafe-supply", label: "توريد الكافيهات" },
    { value: "bakery", label: "مخبوزات يومية" },
    { value: "desserts", label: "حلويات" },
    { value: "other", label: "أخرى" },
  ]

  return (
    <div className="min-h-screen bg-background" dir="rtl">
        <motion.section
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="relative py-20 lg:py-32 overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-b from-muted/30 via-transparent to-background" />
          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              variants={fadeInUp}
              className="text-center max-w-3xl mx-auto mb-16"
            >
              <h1 className="text-5xl lg:text-7xl font-bold text-foreground mb-6">
                تواصل معنا
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed">
                نحن هنا لخدمتك. تواصل معنا لأي استفسار أو طلب عرض سعر
              </p>
            </motion.div>

            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 max-w-7xl mx-auto">
              <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="space-y-8">
                <Card className="bg-card border-border shadow-sm">
                  <CardContent className="p-8">
                    <h2 className="text-3xl font-bold text-foreground mb-8">معلومات التواصل</h2>
                    <div className="space-y-6">
                      {contactInfo.map((item, index) => {
                        const Icon = item.icon
                        return (
                          <motion.a
                            key={index}
                            variants={staggerItem}
                            href={item.link}
                            className="flex items-start gap-4 p-4 rounded-xl hover:bg-muted/50 transition-colors group"
                          >
                            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                              <Icon className="w-6 h-6 text-accent" />
                            </div>
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold text-foreground mb-1">
                                {item.titleAr}
                              </h3>
                              <p className="text-muted-foreground">{item.valueAr}</p>
                            </div>
                          </motion.a>
                        )
                      })}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-card border-border shadow-sm">
                  <CardContent className="p-8">
                    <h2 className="text-3xl font-bold text-foreground mb-6">تابعنا</h2>
                    <div className="flex gap-4">
                      <a
                        href="https://wa.me/966550096297"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 rounded-full bg-[#25D366] flex items-center justify-center hover:scale-110 transition-transform"
                      >
                        <SiWhatsapp className="w-6 h-6 text-white" />
                      </a>
                      <a
                        href="https://www.facebook.com/people/%D8%A3%D8%B7%D9%8A%D8%A7%D9%81-%D8%A7%D9%84%D8%AE%D8%A8%D8%A7%D8%B2%D9%8A%D9%86/pfbid05V2kvVKfZ4kPnQAtdWS58PhA32UAMnzxoUSDdpx85TL7nBRUzeh5wfs2pcA6UyfGl/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 rounded-full bg-[#1877F2] flex items-center justify-center hover:scale-110 transition-transform"
                      >
                        <SiFacebook className="w-6 h-6 text-white" />
                      </a>
                      <a
                        href="https://www.instagram.com/atiafcatering.sa/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 rounded-full bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#F77737] flex items-center justify-center hover:scale-110 transition-transform"
                      >
                        <SiInstagram className="w-6 h-6 text-white" />
                      </a>
                      <a
                        href="#"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 rounded-full bg-foreground flex items-center justify-center hover:scale-110 transition-transform"
                      >
                        <SiX className="w-5 h-5 text-background" />
                      </a>
                      <a
                        href="https://www.snapchat.com/@atiafelkhbazin1"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 rounded-full bg-[#FFFC00] flex items-center justify-center hover:scale-110 transition-transform"
                      >
                        <SiSnapchat className="w-6 h-6 text-black" />
                      </a>
                    </div>
                  </CardContent>
                </Card>

                <motion.div variants={fadeInUp} className="relative">
                  <Card className="bg-gradient-to-br from-accent/10 to-accent/5 border-accent/20 shadow-lg overflow-hidden">
                    <CardContent className="p-8">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-16 h-16 rounded-full bg-[#25D366] flex items-center justify-center">
                          <MessageCircle className="w-8 h-8 text-white" />
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-foreground">تواصل سريع عبر واتساب</h3>
                          <p className="text-muted-foreground">رد فوري على استفساراتك</p>
                        </div>
                      </div>
                      <Button
                        asChild
                        size="lg"
                        className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white text-lg h-14"
                      >
                        <a href="https://wa.me/966550096297" target="_blank" rel="noopener noreferrer">
                          <SiWhatsapp className="w-6 h-6 ml-2" />
                          ابدأ المحادثة الآن
                        </a>
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              </motion.div>

              <motion.div variants={fadeInUp} initial="hidden" animate="visible">
                <Card className="bg-card border-border shadow-sm">
                  <CardContent className="p-8">
                    <h2 className="text-3xl font-bold text-foreground mb-8">أرسل لنا رسالة</h2>
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
                          الاسم الكامل *
                        </label>
                        <Input
                          id="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="h-12 bg-background border-border focus:border-accent focus:ring-accent"
                          placeholder="أدخل اسمك الكامل"
                        />
                      </div>

                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-2">
                          رقم الجوال *
                        </label>
                        <Input
                          id="phone"
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="h-12 bg-background border-border focus:border-accent focus:ring-accent"
                          placeholder="05xxxxxxxx"
                        />
                      </div>

                      <div>
                        <label htmlFor="company" className="block text-sm font-medium text-foreground mb-2">
                          اسم الشركة أو المؤسسة
                        </label>
                        <Input
                          id="company"
                          type="text"
                          value={formData.company}
                          onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                          className="h-12 bg-background border-border focus:border-accent focus:ring-accent"
                          placeholder="اختياري"
                        />
                      </div>

                      <div>
                        <label htmlFor="serviceType" className="block text-sm font-medium text-foreground mb-2">
                          نوع الخدمة المطلوبة *
                        </label>
                        <Select
                          value={formData.serviceType}
                          onValueChange={(value) => setFormData({ ...formData, serviceType: value })}
                          required
                        >
                          <SelectTrigger className="h-12 bg-background border-border focus:border-accent focus:ring-accent">
                            <SelectValue placeholder="اختر نوع الخدمة" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">اختر نوع الخدمة</SelectItem>
                            {serviceOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
                          رسالتك *
                        </label>
                        <Textarea
                          id="message"
                          required
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                          className="min-h-[150px] bg-background border-border focus:border-accent focus:ring-accent resize-none"
                          placeholder="اكتب تفاصيل طلبك أو استفسارك هنا..."
                        />
                      </div>

                      <Button
                        type="submit"
                        size="lg"
                        className="w-full bg-primary hover:bg-primary/90 text-primary-foreground h-14 text-lg"
                      >
                        <Send className="w-5 h-5 ml-2" />
                        إرسال الرسالة
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            <motion.div variants={fadeInUp} initial="hidden" animate="visible" className="mt-16">
              <Card className="bg-card border-border shadow-sm overflow-hidden">
                <CardContent className="p-8">
                  <div className="flex flex-col items-center justify-center gap-4 py-8">
                    <MapPin className="w-12 h-12 text-accent" />
                    <h3 className="text-2xl font-bold text-foreground">موقعنا</h3>
                    <p className="text-muted-foreground mb-4">الرياض، المملكة العربية السعودية</p>
                    <a
                      href="https://share.google/eEZdCNPnQcUCak0tN"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-accent text-white font-bold px-8 py-4 rounded-full hover:bg-accent/90 transition-colors text-lg shadow-lg"
                    >
                      <MapPin className="w-5 h-5" />
                      اضغط هنا لعرض الموقع على الخريطة
                    </a>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </motion.section>
      </div>
  )
}
